import React, { useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '../Button/Button';
import { Loader2 } from 'lucide-react';

let toasterInstanceCounter = 0;

export const Toaster = ({ message, type = 'default', onConfirm, onCancel, confirmLoading = false }) => {
  // Unique per mounted instance. Two Toasters can be mounted at once (e.g. a
  // page's action feedback plus ProfileCard's own errors); react-toastify v10
  // keys its container registry by id, so two id-less containers (both
  // default id 1) overwrite each other and the orphaned one crashes with
  // "Cannot set properties of undefined (setting 'toggle')". Targeting every
  // call by a per-instance containerId also stops one Toaster's dismiss from
  // wiping other containers' toasts (e.g. the comms notification toasts).
  const containerIdRef = useRef(null);
  if (containerIdRef.current === null) {
    toasterInstanceCounter += 1;
    containerIdRef.current = `toaster-${toasterInstanceCounter}`;
  }
  const containerId = containerIdRef.current;

  const displayToast = () => {
    if (!message) return;

    toast.dismiss({ containerId }); // Clear any existing toast in THIS container

    if (onConfirm && onCancel) {
      // Clicking react-toastify's own close (X) button dismisses the toast
      // directly — it never runs our onConfirm/onCancel, so a caller gating
      // buttons on "is this confirm still open" would be stuck forever with
      // no way to signal it's done. `onClose` fires on EVERY dismissal path
      // (X, swipe, or our own toast.dismiss below); `handled` distinguishes
      // "the user already told us via a button" from "it just went away" —
      // only the latter falls back to onCancel.
      let handled = false;
      const toastId = toast(
        ({ closeToast: _closeToast }) => (
          <div className="bg-white p-1">
            <p className="text-gray-800">{message}</p>
            <div className="flex justify-center space-x-4 mt-2">
              <Button
                text={confirmLoading ? 'Deleting…' : 'Yes'}
                icon={confirmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                onClick={() => {
                  if (confirmLoading) return;
                  handled = true;
                  toast.dismiss(toastId);
                  onConfirm?.();
                }}
                disabled={confirmLoading}
                className="bg-orange-primary text-white hover:bg-red-500"
              />
              <Button
                text="No"
                onClick={() => {
                  if (confirmLoading) return;
                  handled = true;
                  toast.dismiss(toastId);
                  onCancel?.();
                }}
                disabled={confirmLoading}
                className="bg-orange-primary text-white hover:bg-gray-400"
              />
            </div>
          </div>
        ),
        {
          autoClose: false,
          closeOnClick: false,
          containerId,
          icon: false,
          type: 'default',
          onClose: () => {
            if (!handled) onCancel?.();
          },
        }
      );
    } else {
      toast(message, {
        autoClose: 5000,
        type: type, // 'success' | 'error' | 'warning' | 'info' | 'default'
        containerId,
      });
    }
  };
  useEffect(() => {
    displayToast();
    return () => toast.dismiss({ containerId }); // cleanup on unmount (this container only)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- displayToast reads message/type/onConfirm/onCancel/confirmLoading
  }, [message, type, onConfirm, onCancel, confirmLoading]);

  return (
    <ToastContainer
      containerId={containerId}
      limit={1}
      theme="light"
      style={{ pointerEvents: 'none' }}
      toastStyle={{ pointerEvents: 'auto' }}
    />
  );
};
